/**
 * Расчеты для калькуляторов здоровья
 */

/**
 * Расчет индекса массы тела (ИМТ)
 * 
 * @param {number} weight - Вес в килограммах
 * @param {number} height - Рост в сантиметрах
 * @returns {number} Индекс массы тела
 */
export const calculateBMI = (weight, height) => {
  // Переводим рост из сантиметров в метры
  const heightInMeters = height / 100;
  return weight / (heightInMeters * heightInMeters);
};

/**
 * Получение категории ИМТ
 * 
 * @param {number} bmi - Индекс массы тела
 * @returns {Object} Категория ИМТ и рекомендации
 */
export const getBMICategory = (bmi) => {
  if (bmi < 16) {
    return {
      category: 'Выраженный дефицит массы тела',
      description: 'Значительное недоедание или медицинские проблемы',
      recommendation: 'Срочно обратитесь к врачу для проверки здоровья и разработки плана набора веса.'
    };
  } else if (bmi < 18.5) {
    return {
      category: 'Недостаточная масса тела',
      description: 'Ниже нормального диапазона',
      recommendation: 'Рассмотрите возможность увеличения калорийности питания и обратитесь к диетологу.'
    };
  } else if (bmi < 25) {
    return {
      category: 'Нормальная масса тела',
      description: 'Здоровый диапазон',
      recommendation: 'Поддерживайте текущий вес с помощью сбалансированного питания и регулярной физической активности.'
    };
  } else if (bmi < 30) {
    return {
      category: 'Избыточная масса тела',
      description: 'Предожирение',
      recommendation: 'Рассмотрите возможность небольшого снижения веса через здоровое питание и увеличение физической активности.'
    };
  } else if (bmi < 35) {
    return {
      category: 'Ожирение I степени',
      description: 'Умеренное ожирение',
      recommendation: 'Обратитесь к врачу для оценки рисков здоровья и разработки плана снижения веса.'
    };
  } else if (bmi < 40) {
    return {
      category: 'Ожирение II степени',
      description: 'Выраженное ожирение',
      recommendation: 'Настоятельно рекомендуется посетить врача для комплексной оценки здоровья и лечения.'
    };
  } else {
    return {
      category: 'Ожирение III степени',
      description: 'Морбидное ожирение',
      recommendation: 'Требуется срочное медицинское вмешательство для снижения рисков для здоровья.'
    };
  }
};

/**
 * Расчет идеального веса по разным формулам
 * 
 * @param {number} height - Рост в сантиметрах
 * @param {string} gender - Пол ('male' или 'female')
 * @param {string} formula - Формула для расчета ('broca', 'devine', 'robinson', 'miller', 'hamwi')
 * @returns {number} Идеальный вес в килограммах
 */
export const calculateIdealWeight = (height, gender, formula = 'devine') => {
  const isMale = gender === 'male';
  
  switch (formula) {
    // Формула Брока
    case 'broca':
      return isMale
        ? height - 100 - (height - 100) * 0.1
        : height - 100 - (height - 100) * 0.15;
    
    // Формула Девина
    case 'devine':
      return isMale
        ? 50 + 2.3 * ((height / 2.54) - 60)
        : 45.5 + 2.3 * ((height / 2.54) - 60);
    
    // Формула Робинсона
    case 'robinson':
      return isMale
        ? 52 + 1.9 * ((height / 2.54) - 60)
        : 49 + 1.7 * ((height / 2.54) - 60);
    
    // Формула Миллера
    case 'miller':
      return isMale
        ? 56.2 + 1.41 * ((height / 2.54) - 60)
        : 53.1 + 1.36 * ((height / 2.54) - 60);
    
    // Формула Хамви
    case 'hamwi':
      return isMale
        ? 48 + 2.7 * ((height / 2.54) - 60)
        : 45.5 + 2.2 * ((height / 2.54) - 60);
      
    default:
      return calculateIdealWeight(height, gender, 'devine');
  }
};

/**
 * Расчет дневной нормы калорий
 * 
 * @param {number} weight - Вес в килограммах
 * @param {number} height - Рост в сантиметрах
 * @param {number} age - Возраст в годах
 * @param {string} gender - Пол ('male' или 'female')
 * @param {string} activityLevel - Уровень активности ('sedentary', 'light', 'moderate', 'active', 'very_active')
 * @returns {number} Дневная норма калорий
 */
export const calculateCalories = (weight, height, age, gender, activityLevel) => {
  // Коэффициенты активности
  const activityMultipliers = {
    sedentary: 1.2,      // Сидячий образ жизни, минимум физической активности
    light: 1.375,        // Легкая активность (легкие упражнения/спорт 1-3 дня в неделю)
    moderate: 1.55,      // Умеренная активность (умеренные упражнения/спорт 3-5 дней в неделю)
    active: 1.725,       // Интенсивная активность (интенсивные упражнения/спорт 6-7 дней в неделю)
    very_active: 1.9     // Очень интенсивная активность (физический труд или 2x в день тренировки)
  };
  
  // Расчет базового метаболизма по формуле Миффлина-Сан Жеора
  let bmr;
  if (gender === 'male') {
    bmr = 10 * weight + 6.25 * height - 5 * age + 5;
  } else {
    bmr = 10 * weight + 6.25 * height - 5 * age - 161;
  }
  
  // Умножаем на коэффициент активности
  return Math.round(bmr * (activityMultipliers[activityLevel] || 1.2));
};

/**
 * Расчет целевых зон пульса
 * 
 * @param {number} age - Возраст в годах
 * @param {number} restingHeartRate - Пульс в покое (опционально)
 * @returns {Object} Зоны пульса
 */
export const calculateHeartRateZones = (age, restingHeartRate = null) => {
  // Максимальный пульс по формуле Танака
  const maxHeartRate = 208 - 0.7 * age;
  
  // Расчет резерва сердечного ритма (метод Карвонена)
  const getHeartRate = (percent) => {
    if (restingHeartRate !== null) {
      return Math.round(restingHeartRate + (maxHeartRate - restingHeartRate) * (percent / 100));
    }
    return Math.round(maxHeartRate * (percent / 100));
  };
  
  return {
    max: Math.round(maxHeartRate),
    zones: [
      {
        name: 'Очень легкая активность',
        description: 'Разминка, восстановление',
        min: getHeartRate(50),
        max: getHeartRate(60)
      },
      {
        name: 'Легкая активность',
        description: 'Сжигание жира, базовая выносливость',
        min: getHeartRate(60),
        max: getHeartRate(70)
      },
      {
        name: 'Умеренная активность',
        description: 'Аэробная тренировка, улучшение выносливости',
        min: getHeartRate(70),
        max: getHeartRate(80)
      },
      {
        name: 'Тяжелая активность',
        description: 'Анаэробная тренировка, повышение результативности',
        min: getHeartRate(80),
        max: getHeartRate(90)
      },
      {
        name: 'Максимальная активность',
        description: 'Максимальная нагрузка, только для тренированных',
        min: getHeartRate(90),
        max: getHeartRate(100)
      }
    ]
  };
};

/**
 * Расчет соотношения талии к бедрам (WHR)
 * 
 * @param {number} waist - Обхват талии
 * @param {number} hip - Обхват бедер
 * @returns {number} Индекс WHR
 */
export const calculateWHR = (waist, hip) => {
  if (!waist || !hip || waist <= 0 || hip <= 0) {
    throw new Error('Значения талии и бедер должны быть положительными числами');
  }
  
  return waist / hip;
};

/**
 * Получение категории WHR
 * 
 * @param {number} whr - Значение WHR
 * @param {string} gender - Пол ('male' или 'female')
 * @param {Object} categories - Объект с категориями WHR для каждого пола
 * @returns {Object} Категория WHR
 */
export const getWHRCategory = (whr, gender, categories) => {
  // Получаем категории для выбранного пола
  const genderCategories = categories[gender] || [];
  
  // Находим подходящую категорию
  for (const category of genderCategories) {
    if (
      (category.min === undefined && whr <= category.max) ||
      (category.max === undefined && whr >= category.min) ||
      (category.min !== undefined && category.max !== undefined && 
       whr >= category.min && whr <= category.max)
    ) {
      return category;
    }
  }
  
  // Если категория не найдена, возвращаем категорию по умолчанию
  return {
    id: 'unknown',
    name: 'Неопределенная категория',
    color: '#9e9e9e',
    risk: 'Невозможно определить риск из-за нестандартного значения'
  };
};

/**
 * Расчет соотношения талии к росту (WHtR)
 * 
 * @param {number} waist - Обхват талии
 * @param {number} height - Рост
 * @returns {number} Индекс WHtR
 */
export const calculateWHtR = (waist, height) => {
  if (!waist || !height || waist <= 0 || height <= 0) {
    throw new Error('Значения талии и роста должны быть положительными числами');
  }
  
  return waist / height;
};

/**
 * Оценка риска на основе WHtR
 * 
 * @param {number} whtr - Значение WHtR
 * @returns {Object} Оценка риска
 */
export const getWHtRRisk = (whtr) => {
  if (whtr < 0.4) {
    return {
      category: 'Недостаточный вес',
      risk: 'Низкий риск, но возможны проблемы, связанные с недостаточным весом',
      color: '#ffa726'
    };
  } else if (whtr < 0.5) {
    return {
      category: 'Нормальный вес',
      risk: 'Минимальный риск для здоровья',
      color: '#4caf50'
    };
  } else if (whtr < 0.6) {
    return {
      category: 'Избыточный вес',
      risk: 'Повышенный риск сердечно-сосудистых заболеваний и диабета',
      color: '#ffa726'
    };
  } else {
    return {
      category: 'Ожирение',
      risk: 'Высокий риск сердечно-сосудистых заболеваний, диабета и метаболического синдрома',
      color: '#d32f2f'
    };
  }
};